import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, Search } from 'lucide-react';
import React from 'react'
import { getThreeDxProject, type ThreeDxProjectRequestError, type ThreeDxProjectResponse } from '@/services/ThreeDxProjectService';

type ModalContentProps = {
    modalSearchValue: string;
    setModalSearchValue: React.Dispatch<React.SetStateAction<string>>;
    apiData: ThreeDxProjectResponse | null;
    setApiData: React.Dispatch<React.SetStateAction<ThreeDxProjectResponse | null>>;
}

const ModalContent = ({ modalSearchValue, setModalSearchValue, apiData, setApiData }: ModalContentProps) => {
    const [hasSubmittedSearch, setHasSubmittedSearch] = React.useState(false);
    const [loader, setLoader] =  React.useState(false);
    const [notFoundMessage, setNotFoundMessage] = React.useState('');

    const handleSearchClick = async () => {
        setApiData(null);
        
        setLoader(true);
        try {
            setApiData(await getThreeDxProject(modalSearchValue));
        } catch (error) {
            console.error("Failed to fetch 3DX project", error);
            setApiData(null);
            const requestError = error as ThreeDxProjectRequestError;
            if ((requestError?.status === 404 || requestError?.status === 503) && requestError.message) {
                setNotFoundMessage(requestError.message);
            }
            setLoader(false);
        } finally {
            setLoader(false);
            setHasSubmittedSearch(true);
        }
    };

    const importedProject = apiData?.data?.[0];
    const projectInfo = importedProject?.project_info;
    const metadata = Object.entries(projectInfo ?? {}).filter(
        ([key]) => !['projectName', 'projectID', 'createdAt', 'updatedAt', 'projectDescription', 'project_users', 'createdBy'].includes(key)
    );

    const formatMetadataLabel = (key: string) => key.replace(/([A-Z])/g, ' $1').replace(/^./, (value) => value.toUpperCase());
    const formatMetadataValue = (value: unknown) => Array.isArray(value) ? value.join(', ') : String(value ?? '');

    return (
        <>
            <div className="p-2 flex flex-wrap gap-5 w-full items-end">
                {/* Search Section  Start */}
                <div className="flex flex-col  relative w-fit">
                    <div className=' font-medium'>3DX DTR Id</div>
                    <div className='relative w-fit'>
                        <Input
                            placeholder="Enter 3DX DTR Id"
                            value={modalSearchValue ? modalSearchValue : ""}
                            onChange={(event) => {
                                if (setModalSearchValue) {
                                    setModalSearchValue(event.target.value);
                                }
                                setApiData(null);
                                setHasSubmittedSearch(false);
                                setNotFoundMessage('');
                            }}
                            className="h-8 w-5/6 lg:w-[400px] pl-8 rounded-sm border border-r-1"
                        />
                        <Search
                            className="absolute left-2 top-2 text-gray-500"
                            size={16}
                        />
                    </div>
                </div>
                <div className="flex items-center gap-4">
                    <Button
                        onClick={handleSearchClick}
                        disabled={!modalSearchValue}
                        className="bg-blue text-white hover:bg-[#003B7D]"
                        variant={modalSearchValue ? "default" : "ghost"}
                    >
                        Submit
                    </Button>
                </div>
                {/* Search Section  End */}

                {
                    loader && (
                        <div className="w-full flex items-center gap-2 text-gray-500">
                            <Loader2 className="h-4 w-4 animate-spin" />
                                Loading project...
                        </div>
                    )
                }


                {/* Project Details Section Start */}
                
                {
                    modalSearchValue && apiData ? (
                        <div className="project-details grid w-full grid-cols-1 gap-4 lg:grid-cols-2 hidde">
                            <div className="rounded-md border border-border-style-1 bg-white p-4 shadow-sm">
                                <div className="flex items-start justify-between gap-3 border-b border-border-style-1 pb-3">
                                    <div>
                                        <p className="text-xs font-semibold uppercase text-gray-500">Project Name</p>
                                        <h3 className="mt-1 text-base font-semibold text-blue">
                                            {projectInfo?.projectName}
                                        </h3>
                                    </div>
                                    <span className="shrink-0 rounded-full bg-light-blue px-3 py-1 text-xs font-semibold text-blue">
                                        {importedProject?.dtrStatus}
                                    </span>
                                </div>

                                <div className="grid grid-cols-1 gap-3 pt-3 text-sm sm:grid-cols-2">
                                    <div>
                                        <p className="text-xs font-medium text-gray-500">Project ID</p>
                                        <p className="font-semibold text-gray-800">{projectInfo?.projectID}</p>
                                    </div>

                                    <div>
                                        <p className="text-xs font-medium text-gray-500">Created By</p>
                                        <p className="font-semibold text-gray-800">{projectInfo?.createdBy}</p>
                                    </div>

                                    <div>
                                        <p className="text-xs font-medium text-gray-500">Created At</p>
                                        <p className="font-semibold text-gray-800">{projectInfo?.createdAt}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs font-medium text-gray-500">Updated At</p>
                                        <p className="font-semibold text-gray-800">{projectInfo?.updatedAt}</p>
                                    </div>
                                </div>

                                <div className="mt-3 text-sm text-gray-600">
                                    <p className="text-xs font-medium text-gray-600">Project Description</p>
                                    <p className="text-sm font-semibold text-gray-800 ">
                                        {projectInfo?.projectDescription}
                                    </p>
                                </div>

                                <div className="mt-3">
                                    <p className="text-xs font-medium text-gray-500">Project Users</p>
                                    <p className="text-sm font-semibold text-gray-800 ">
                                        {projectInfo?.project_users?.join(", ")}
                                    </p>
                                </div>
                            </div>

                            <div className="rounded-md border border-blue/30 bg-light-blue/20 p-4 shadow-sm">
                                <div className="border-b border-blue/20 pb-3">
                                    <h3 className="text-base font-semibold text-blue">Project Metadata</h3>
                                    <p className="mt-1 text-xs text-gray-600">Values returned by 3DX</p>
                                </div>

                                <div className="grid grid-cols-1 gap-3 pt-3 sm:grid-cols-2">
                                    {metadata.map(([key, value]) => (
                                        <div key={key}>
                                            <p className="text-xs font-medium text-gray-500">{formatMetadataLabel(key)}</p>
                                            <p className="text-sm font-semibold text-gray-800">{formatMetadataValue(value)}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ) : !loader && hasSubmittedSearch && modalSearchValue && !apiData && (<div className="project-details grid w-full grid-cols-1 gap-4 lg:grid-cols-2 text-[red]">{notFoundMessage}</div>)
                }
            </div>
        </>
    );
}

export default ModalContent
